import { create } from 'zustand';
import { createInternalTalent, editTalent } from '@libs/api/talents';
import { parseError } from '@libs/api/errors';
import { isAxiosError } from 'axios';
import { getInternalTalent } from '@libs/api/talents/single';
import { StaffCreateData, StaffDetails, StaffEditData } from '@libs/models/talents/talent';

type InitialState = {
  createTalent: (data: StaffCreateData) => Promise<void>;
  saveTalent: (id: string, data: StaffEditData) => Promise<void>;
  saving: boolean;
  showEditForm: boolean;
  showCreateForm: boolean;
  openCreateForm: () => void;
  openEditForm: (id: string) => Promise<void>;
  editInitial: StaffDetails | null;
  editLoading: boolean;
  closeEditForm: () => void;
  reloadCurrentTalentData: (userId: string, silent?: boolean) => Promise<void>;
};

export const useEditTalentStore = create<InitialState>()((set) => ({
  saving: false,
  editInitial: null,
  editLoading: false,
  createTalent: async (data) => {
    set({ saving: true });
    try {
      await createInternalTalent(data);
    } catch (error) {
      throw parseError(error, 'talents.editForm.errors');
    } finally {
      set({ saving: false });
    }
  },
  saveTalent: async (id, data) => {
    set({ saving: true });
    try {
      await editTalent(id, data);
    } catch (error) {
      if (isAxiosError(error)) {
        throw error.response?.data;
      }
      throw error;
    } finally {
      set({ saving: false });
    }
  },
  showCreateForm: false,
  showEditForm: false,
  openCreateForm: () => {
    set({ showCreateForm: true });
  },
  closeEditForm: () => {
    set({ showEditForm: false, showCreateForm: false, editLoading: false, editInitial: null });
  },
  openEditForm: async (id) => {
    set({ showEditForm: true, editLoading: true });
    const talent = await getInternalTalent(id);
    set({
      editLoading: false,
      editInitial: talent,
    });
  },
  reloadCurrentTalentData: async (userId: string, silent: boolean = false) => {
    !silent && set({ editLoading: true });
    const talent = await getInternalTalent(userId);
    set({
      editLoading: false,
      editInitial: talent,
    });
  },
}));
